import { gql } from '@apollo/client'
import { RichText } from '@farol-ds/react'
import { ARTIFACT_LABEL, layoutStyles } from '@jusbrasil-web/docview/core'
import { RefObject, useEffect, useRef, useState } from 'react'
import { createRoot, Root } from 'react-dom/client'

import { PLACEHOLDER_BANNER_NAME } from '../../../constants'
import { useSdDocviewChatsTracking } from '../../../experiment'
import { useSegmentation } from '../../../hooks/segmentation-hook'
import { hadleBannerSpacing, parseBannerPlaceholder, parseVideos } from '../../../utils'
import { OrientationBanner } from '../../orientation-banner'
import { Summary } from '../../summary'
import { ArticleNewsDocument_DocumentFragment } from './__generated__/article-news-document.graphql'
import styles from './article-news-document.module.scss'

export interface ArticleDocumentProps {
  document: ArticleNewsDocument_DocumentFragment
  contentRef: RefObject<HTMLDivElement>
  isExperimentArticle?: boolean
  docId?: number | null
}

export function ArticleNewsDocument(props: ArticleDocumentProps) {
  const { document, contentRef, isExperimentArticle = false, docId = null } = props

  const [currentDocument, setCurrentDocument] =
    useState<ArticleNewsDocument_DocumentFragment>(document)
  const orientationRootRef = useRef<Root | null>(null)
  const segmentationData = useSegmentation()
  const tracking = useSdDocviewChatsTracking({ isExperimentArticle })

  useEffect(() => {
    if (!document.text) return

    let documentText = document.text
    if (
      document.artifact === ARTIFACT_LABEL.ARTIGO &&
      segmentationData?.typeSegmentation === 'SEUS_DIREITOS'
    ) {
      documentText = parseBannerPlaceholder(document.text, PLACEHOLDER_BANNER_NAME)
    }

    const textWithBannerAndVideos = parseVideos(
      documentText,
      styles['embedded-video-wrapper'],
      styles['embedded-video']
    )
    setCurrentDocument({ ...document, text: textWithBannerAndVideos })
  }, [document, segmentationData?.typeSegmentation])

  useEffect(() => {
    const rootEl = contentRef.current
    if (!rootEl) return

    const host = rootEl.querySelector(`[${PLACEHOLDER_BANNER_NAME}]`) as HTMLElement
    if (!host) return

    if (!orientationRootRef.current) {
      orientationRootRef.current = createRoot(host)
    }

    orientationRootRef.current.render(<OrientationBanner tracking={tracking} docId={docId} />)

    hadleBannerSpacing(host)

    return () => {
      orientationRootRef.current?.unmount()
      orientationRootRef.current = null
    }
  }, [currentDocument.text, contentRef, tracking, docId])

  return (
    <>
      <Summary document={currentDocument} />

      <RichText ref={contentRef} className={layoutStyles.contentWrapper}>
        <div dangerouslySetInnerHTML={{ __html: currentDocument.text || '' }} />
      </RichText>
    </>
  )
}

ArticleNewsDocument.fragments = {
  document: gql`
    fragment ArticleNewsDocument_document on Document {
      text(highlight: true)

      ...Summary_document
    }

    ${Summary.fragments.document}
  `,
}
